import { useCallback } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, TextField } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { SchoolMapper } from '@/shared/category/util';
import { cn } from '@/utils/tailwind.util';
import { type CategoryDisplayText } from '@/views/build/features/category-display-text';
import { type CategorySelectorState, ColumnUI } from '@/views/build/features/category-select-view';
import { CUSTOM_PUBLISHER_MAX_LENGTH, CUSTOM_PUBLISHER_MIN_LENGTH } from '@/views/build/form/schema/BuildPublisherInfoSchema';
import { isAllGrades } from '@/views/build/util';
import { NONE_VALUE } from '../../category-tree';
export const PublisherSelection = ({
  categoryInfo,
  displayText
}: {
  categoryInfo: CategorySelectorState;
  displayText: CategoryDisplayText;
}) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const isActive = !!categoryInfo.publisher.publishers && !!categoryInfo.subject.selectedSubjectNode && !isAllGrades(categoryInfo.grade.selectedGradeNode?.groupId);
  const publisher = categoryInfo.publisher.selectedPublisher?.nodeId;
  const customPublisher = categoryInfo.publisher.selectedCustomPublisher;
  const options = categoryInfo.publisher.publishers;
  const error = categoryInfo.publisher.errors.customPublisher;
  const handleChange = useCallback((value: string) => {
    const node = options?.find(option => option.nodeId === value);
    node && categoryInfo.updateCategoryInfo(node);
  }, [categoryInfo, options]);
  const handleCustomPublisherChange = useCallback((value: string) => {
    categoryInfo.updateCustomPublisher(value);
  }, [categoryInfo]);
  const handleHover = useCallback((value: string, label: string) => {
    displayText.updateHoverPreview({
      field: 'publisher',
      value,
      label
    });
  }, [displayText]);
  const handleHoverLeave = useCallback(() => {
    displayText.clearHoverPreview('publisher');
  }, [displayText]);
  const isSelected = useCallback((nodeId: string) => {
    if (!publisher) return nodeId === NONE_VALUE;
    return publisher === nodeId;
  }, [publisher]);
  return <div className={cn('flex min-h-0 min-w-0 h-full flex-1 flex-col gap-4xs', !isActive && 'opacity-[0.32]')} data-sentry-component="PublisherSelection" data-sentry-source-file="PublisherSelection.tsx">
      <ColumnUI.Header label={t('build:schoolInfo.textbookPublisher')} error={error} data-sentry-element="unknown" data-sentry-source-file="PublisherSelection.tsx" />
      <div className="min-h-0 flex-1 overflow-auto">
        {isActive && <Accordion type="single" collapsible value={publisher} onValueChange={handleChange}>
            {options?.map(option => {
          return <AccordionItem key={option.nodeId} value={option.nodeId} className="border-none">
                  <AccordionTrigger asChild className="hover:no-underline [&[data-state=open]>svg]:rotate-0">
                    <ColumnUI.SelectButton className="font-[600]" isSelected={isSelected(option.nodeId)} onMouseEnter={() => handleHover(option.nodeId, option.label)} onMouseLeave={handleHoverLeave}>
                      {option.label}
                    </ColumnUI.SelectButton>
                  </AccordionTrigger>
                  {SchoolMapper.isEtc(option.nodeId) && <AccordionContent asChild className="mb-2xs rounded-sm border border-opacity pb-0 shadow-xs">
                      <div className="p-2xs">
                        <TextField.Root required>
                          <TextField.Input placeholder={t('build:schoolInfo.textbookName')} onChange={e => handleCustomPublisherChange(e.target.value)} value={customPublisher} maxLength={CUSTOM_PUBLISHER_MAX_LENGTH} minLength={CUSTOM_PUBLISHER_MIN_LENGTH} autoFocus />
                        </TextField.Root>
                      </div>
                    </AccordionContent>}
                </AccordionItem>;
        })}
          </Accordion>}
      </div>
    </div>;
};